import React from "react";
import { SOCIAL, type SocialLink } from "@/data/social";
import {
  Github,
  Linkedin,
  Youtube,
  type LucideIcon,
} from "lucide-react";

const Icons: Record<string, LucideIcon> = {
  github: Github,
  linkedin: Linkedin,
  youtube: Youtube,
};

/**
 * SocialRow — renders the footer social strip. Live accounts get active
 * links; not-yet-live accounts render as disabled chips with a tooltip,
 * so the IA is visible but users don't land on 404s.
 */
export function SocialRow() {
  return (
    <ul className="flex items-center gap-2 flex-wrap" aria-label="Social accounts">
      {SOCIAL.map((s) => (
        <li key={s.id}>
          <SocialChip link={s} />
        </li>
      ))}
    </ul>
  );
}

function SocialChip({ link }: { link: SocialLink }) {
  const Icon = Icons[link.id];
  const label = link.live ? link.label : `${link.label} (coming soon)`;
  const classes =
    "inline-flex items-center gap-1.5 border px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors";

  if (link.live) {
    return (
      <a
        href={link.url}
        target="_blank"
        rel="noopener noreferrer me"
        aria-label={`${link.label} — ${link.handle}`}
        className={`${classes} border-hairline text-ink hover:border-ink hover:bg-paper-2`}
      >
        {Icon ? <Icon size={12} aria-hidden="true" /> : null}
        <span>{link.label}</span>
      </a>
    );
  }
  return (
    <span
      aria-label={label}
      title={label}
      className={`${classes} border-hairline text-ink-3 bg-paper-2 cursor-not-allowed opacity-60`}
    >
      {Icon ? <Icon size={12} aria-hidden="true" /> : null}
      <span>{link.label}</span>
    </span>
  );
}
